import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { UserPlus, Copy, RefreshCw, Trash2, Crown, Mail } from "lucide-react";

const statusTone = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  INVITED: "bg-amber-100 text-amber-700",
  SUSPENDED: "bg-orange-100 text-orange-700",
  REMOVED: "bg-red-100 text-red-700",
};

export default function Membros() {
  const { currentOrg, user, refreshOrgs } = useAuth();
  const orgId = currentOrg?.id;
  const isOwner = currentOrg?.role === "OWNER";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("SALES");
  const [saving, setSaving] = useState(false);
  const [transferFor, setTransferFor] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [m, i, r] = await Promise.all([
        api.get(`/organizations/${orgId}/members`),
        api.get(`/organizations/${orgId}/invites`),
        api.get(`/roles`),
      ]);
      setMembers(m.data);
      setInvites(i.data);
      setRoles(r.data.filter((x) => x.code !== "OWNER"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!email.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/organizations/${orgId}/invites`, { email: email.trim(), role });
      if (data.email_sent) toast.success("Convite enviado por e-mail");
      else {
        toast.success("Convite criado. Copie o link para compartilhar.");
        if (data.link) navigator.clipboard?.writeText(data.link).catch(() => {});
      }
      setInviteOpen(false); setEmail(""); setRole("SALES");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (uid, newRole) => {
    try {
      await api.patch(`/organizations/${orgId}/members/${uid}`, { role: newRole });
      toast.success("Perfil atualizado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeMember = async (uid) => {
    try {
      await api.delete(`/organizations/${orgId}/members/${uid}`);
      toast.success("Membro removido");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (id) => {
    try { await api.post(`/organizations/${orgId}/invites/${id}/revoke`); toast.success("Convite revogado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resend = async (id) => {
    try {
      const { data } = await api.post(`/organizations/${orgId}/invites/${id}/resend`);
      if (data.email_sent) toast.success("Convite reenviado");
      else { toast.success("Link atualizado e copiado"); if (data.link) navigator.clipboard?.writeText(data.link).catch(() => {}); }
    } catch (e) { toast.error(apiError(e)); }
  };

  const doTransfer = async () => {
    try {
      await api.post(`/organizations/${orgId}/transfer-ownership`, { user_id: transferFor.user_id });
      toast.success("Propriedade transferida");
      setTransferFor(null);
      await refreshOrgs();
      window.location.reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="membros-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Usuários e Permissões</h1>
          <p className="text-sm text-muted-foreground mt-1">Gerencie a equipe da organização, papéis de acesso e convites.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-open-button">
          <UserPlus className="h-4 w-4 mr-2" /> Convidar usuário
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="px-5 py-3 border-b border-border"><h2 className="font-semibold">Membros</h2></div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead><TableHead>E-mail</TableHead>
              <TableHead>Papel</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.user_id} data-testid={`member-row-${m.email}`}>
                <TableCell className="font-medium">{m.name}{m.role === "OWNER" && <Crown className="inline h-3.5 w-3.5 ml-1 text-amber-500" />}</TableCell>
                <TableCell className="text-muted-foreground">{m.email}</TableCell>
                <TableCell>
                  {m.role === "OWNER" || m.user_id === user?.user_id ? (
                    <Badge variant="secondary">{m.role_label}</Badge>
                  ) : (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                      <SelectTrigger className="w-[170px] h-8" data-testid={`role-select-${m.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${statusTone[m.status] || ""}`}>{m.status}</span></TableCell>
                <TableCell className="text-right">
                  {isOwner && m.role !== "OWNER" && (
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Transferir propriedade" onClick={() => setTransferFor(m)} data-testid={`transfer-${m.email}`}><Crown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Remover" onClick={() => removeMember(m.user_id)} data-testid={`remove-${m.email}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && members.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nenhum membro</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {invites.length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <div className="px-5 py-3 border-b border-border"><h2 className="font-semibold">Convites pendentes</h2></div>
          <Table>
            <TableHeader><TableRow><TableHead>E-mail</TableHead><TableHead>Papel</TableHead><TableHead>Expira</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
            <TableBody>
              {invites.map((i) => (
                <TableRow key={i.id} data-testid={`invite-row-${i.email}`}>
                  <TableCell className="font-medium"><Mail className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />{i.email}</TableCell>
                  <TableCell>{i.role_label}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{(i.expires_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Reenviar / copiar link" onClick={() => resend(i.id)} data-testid={`resend-${i.email}`}><RefreshCw className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Revogar" onClick={() => revoke(i.id)} data-testid={`revoke-${i.email}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convidar usuário</DialogTitle>
            <DialogDescription>Enviaremos um link seguro (válido por 7 dias) para o e-mail informado. Se o envio não estiver disponível, o link é copiado para você compartilhar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pessoa@empresa.com" data-testid="invite-email-input" /></div>
            <div className="space-y-1.5"><Label>Papel</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={sendInvite} disabled={saving} data-testid="invite-send-button"><UserPlus className="h-4 w-4 mr-2" />Enviar convite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!transferFor} onOpenChange={(o) => !o && setTransferFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir propriedade</DialogTitle>
            <DialogDescription>
              Você deixará de ser o proprietário e passará a Administrador. {transferFor?.name} ({transferFor?.email}) se tornará o novo proprietário, com controle total (billing, usuários, exclusão). Esta ação é registrada em auditoria.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferFor(null)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={doTransfer} data-testid="transfer-confirm-button"><Crown className="h-4 w-4 mr-2" />Confirmar transferência</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
